<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Estimate">
      <template>
        <v-progress-linear striped height="6" class="mx-16" v-model="itemProgress" v-if="itemProgress != 100" style="max-width:600px"/>
      </template>
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <v-card width="400">
            <sales-preferences @update="preferences.defaultMessage=$event.defaultMessage;preferences.useNumber=$event.useNumber;preferences.allowDiscounts=$event.allowDiscounts"/>
          </v-card>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row>
            <v-col cols="12" sm="5" md="4">
              <v-row>
                <v-col>
                  <v-autocomplete v-model="estimate.customerId" outlined dense label="Customer" :rules="[selectCustomer]"
                                  :items="customersList" item-text="name" item-value="id" @change="changeCustomer" no-data-text="No customers found"
                                  :hint="customerHint(customer)" persistent-hint ref="customer">
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="customerCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new customer
                      </div>
                    </template>
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">{{customerHint(item)}}</v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="10">
                  <v-text-field label="Email" v-model="estimate.email" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                hint="Separate emails with a comma"/>
                </v-col>
                <v-col cols="2" class="text-caption mt-3">
                  <v-menu :close-on-content-click="false" min-width="350" :nudge-bottom="20">
                    <template v-slot:activator="{ on }">
                      <a href="#" v-on="on">Cc/Bcc</a>
                    </template>
                    <v-card class="pa-4">
                      <v-text-field label="Cc" v-model="estimate.cc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                      <v-text-field label="Bcc" v-model="estimate.bcc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                    </v-card>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Bill to address" v-model="estimate.billToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Ship to address" v-model="estimate.shipToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6" md="5" offset-sm="1">
              <v-row dense>
                <v-col cols="6">
                  <date-picker v-model="estimate.date" label="Date" date-only @change="getExchangeRate()"/>
                </v-col>
                <v-col cols="6" v-if="preferences.useNumber">
                  <v-text-field label="Number" v-model="estimate.number" outlined dense :rules="[fieldRules.required,fieldRules.maxLength(20)]" :counter="20"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="6">
                  <date-picker v-model="estimate.expires" label="Expires" date-only/>
                </v-col>
                <v-col cols="6">
                  <v-select label="Status" outlined dense v-model="estimate.status" :items="['Pending','Accepted','Closed','Rejected']"/>
                </v-col>
              </v-row>
              <v-row dense v-if="salesReps.length > 1">
                <v-col cols="6">
                  <v-autocomplete v-model="estimate.salesRepId" outlined dense label="Sales representative" :disabled="!!restrictedToSalesRep"
                                  :items="salesReps" item-text="name" item-value="id"/>
                </v-col>
              </v-row>
              <v-row dense v-if="connectedToAccounting">
                <v-col cols="6">
                  <v-checkbox v-model="estimate.qbo" label="Create in QBO" class="mt-1"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col class="text-h6 text-right pr-6" v-if="$vuetify.breakpoint.mdAndUp">
              Amount {{customer && this.company.settings.homeCurrency !== customer.currency ? customer.currency : ''}} {{total | currency}}
              <div class="text-body-1" v-if="!preferences.useNumber && estimateNumber">Estimate #{{estimateNumber}}</div>
            </v-col>
          </v-row>

          <v-row dense>
            <v-spacer/>
            <v-col cols="12" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="estimate.taxInclusive" dense hide-details class="mt-0" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <outlined-block v-model="estimate.lines" :rules="[lines]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="estimate.lines" item-key="key" no-data-text="There must be at least one item"
                              :headers="lineHeaders" :expanded.sync="expanded"
                              :item-class="item => item.key === selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n3" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n3" icon @click.stop="deleteLine(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select :menuData="types" :first.sync="item.type" :second.sync="item.id" caption @change="changeLineType(item)" outlined label="Item"/>
                      <template v-if="item.type==='Product'">
                        <v-checkbox dense v-model="item.inBondProduct" class="mt-n4" :disabled="!isProductLoaded(item)"
                                    @change="changeProductBonded(item)">
                          <template v-slot:label><span class="text-caption">Take product from in-bond inventory</span></template>
                        </v-checkbox>
                      </template>
                      <v-text-field dense label="Description" v-model="item.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" placeholder="Description" class="text-caption" outlined/>
                    </template>
                    <div v-else class="pl-1">
                      <div class="text-caption" :class="{'error--text':!item.type || !item.id}">{{itemDescription(item)}}{{item.type==='Product' ? (item.inBondProduct ? ' (in-bond)' : ' (duty-paid)') : ''}}</div>
                      <div :class="{'error--text':!item.description}">{{item.description || 'Description required'}}</div>
                    </div>
                  </template>
                  <template v-slot:item.sku="{item}">
                    <template v-if="!item.items">
                      <v-text-field v-if="selectedItem === item.key" label="SKU" dense outlined v-model="item.sku" class="text-caption" :rules="[fieldRules.maxLength(20)]"/>
                      <div v-else class="pl-1">{{item.sku}}</div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input label="Quantity"
                                             v-model="item.quantity" prefix="" :disabled="isBulkContainer(item)"
                                            :suffix="suffix(item)" :precision="precision(item)" class="text-caption" @change="changeQuantityPrice(item)"
                                            :no-minimum="item.type==='Item'"/>
                      <v-checkbox v-if="item.type==='Product'" dense v-model="item.cases" @change="setProductPrice(item)" class="mt-n4">
                        <template v-slot:label><span class="text-caption">cases</span></template>
                      </v-checkbox>
                    </template>
                    <div v-else class="pr-1">
                      <div :class="{'error--text':item.quantity < 0 && item.type!=='Item'}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                    </div>
                  </template>
                  <template v-slot:item.price="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Unit price" v-model="item.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(item)"/>
                      <div v-else class="pr-1" :class="{'error--text':item.unitPrice<0}">${{item.unitPrice | number(4)}}</div>
                    </template>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="changeAmount(item)" :no-minimum="item.type==='Item'"/>
                      <div v-else class="pr-1" :class="{'error--text':item.amount<0 && item.type!=='Item'}">{{item.amount | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <template v-if="!item.items">
                      <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId" :rules="[connectedToAccounting?fieldRules.required:true]"
                                :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                      <div v-else class="pl-1" :class="{'error--text':connectedToAccounting && !item.taxCodeId}">{{itemTax(item)}}</div>
                    </template>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addLine" :disabled="!addOkay || !edit"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea label="Message to customer" v-model="estimate.memo" outlined dense rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto" class="mt-4"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Private note" rows="2"
                                  v-model="estimate.privateNote" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row v-if="customer && company.settings.homeCurrency !== customer.currency">
                  <v-spacer/>
                  <v-col cols="6" sm="4" md="3">
                    <decimal-number-input class="text-caption" :label="`1 ${customer.currency} =`" outlined v-model="estimate.exchangeRate" prefix="" :suffix="` ${company.settings.homeCurrency}`" :precision="6" hide-details/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2">
              <v-container class="pa-0">
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <v-row dense align="center" v-if="preferences.allowDiscounts || estimate.discount.amount">
                  <v-col cols="5" md="4" offset-md="2">
                    <v-select v-model="estimate.discount.percent" :items="[{text:'Discount percent',value:true},{text:'Discount value',value:false}]"
                              dense outlined hide-details class="text-caption"
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="3" md="2">
                    <decimal-number-input v-model="estimate.discount.amount"
                              :prefix="estimate.discount.percent?'':'$'" :suffix="estimate.discount.percent?'%':''"
                              :max="estimate.discount.percent?100:undefined"
                              hide-details caption
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="4" class="text-right pr-6">{{discountValue?'-':''}}{{discountValue | currency}}</v-col>
                </v-row>
                <template v-for="tax of estimate.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{estimate.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{customer && company.settings.homeCurrency !== customer.currency ? `(${customer.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
                <v-row dense v-if="customer && company.settings.homeCurrency !== customer.currency">
                  <v-col cols="8" class="font-weight-medium text-right">Total ({{company.settings.homeCurrency}})</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total*estimate.exchangeRate | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-switch v-if="estimateId && company.permissions.includes('selling.write')"
                  v-model="edit" label="Edit" hide-details class="mt-0"
                  :disabled="dirty || loading"/>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel" :disabled="loading">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save(false)" :disabled="itemProgress!==100">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <customer-quick-create v-model="customerCreate" @create="newCustomer" :sales-rep-id="restrictedToSalesRep"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns'
import sortableJS from 'sortablejs';
import CustomerQuickCreate from '@/views/sales/customers/CustomerQuickCreate'
import confirmLeave from '@/mixins/confirmLeave'
import customerTypes from '@/mixins/customerTypes'
import createHierarchyList from '@/mixins/createHierarchyList'
import SalesPreferences from '@/views/sales/SalesPreferences'

export default {
  name: 'SalesEstimatesEdit',
  mixins: [confirmLeave,createHierarchyList,customerTypes],
  components: {CustomerQuickCreate,SalesPreferences},
  props: {
    estimateId: {type:String, required:false},
    customerId: {type:String, required:false},
    itemType: {type:String, required:false},
    copyEstimateId: {type:String, required:false}
  },
  data () {
    return {
      preferences: {},
      edit: false,
      province: null,
      taxes: [],
      customers: [],
      salesReps: [{id:'',name:'',email:''}],
      customerCreate:false,
      estimateNumber: null,

      spirits: [],
      wines: [],
      nonExcises: [],
      items: [],
      bundles: [],
      products: [],
      customerTypes: [],
      itemCategories: [],
      productCategories: [],
      estimate: {customerId:'',billToAddress:'',shipToAddress:'',status:'Pending',salesRepId:'',number:'',
                 date:format(new Date(), 'yyyy-MM-dd'), expires:null,exchangeRate:1,
                 lines:[],taxLines:[],memo:'',privateNote:'',taxInclusive:false, qbo:true,
                 discount:{percent:true,amount:0}},
      moveProductToDutyPaid: null,
      showMoveProduct: false,
      dirty: false,
      saving: false,
      loading: false,
      itemProgress: 100,
      valid: true,
      expanded: [],
      moveOffset: 0,
      subTotal: 0,
      selectedItem: null,
      restrictedToSalesRep: '',
      lines: v => v.length>0 || 'There must be at least one line item',
      selectCustomer: v => !!v || 'Select a customer',
      itemValid: id => id !== '' || 'Please select',
      inputLine: null,
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    customer() {
      return this.customers.find(customer => customer.id === this.estimate.customerId);
    },
    customersList() {
      return this.restrictedToSalesRep
              ? this.customers.filter(customer => customer.salesRep?.id === this.restrictedToSalesRep ||
                                                  customer.id === this.estimate.customerId)
              : this.customers;
    },
    types() {
      const types = [];
      types.push({name:'Product',id:'Product',menu:this.menuForProducts(this.products)});
      if (this.bundles.length)
        types.push({name:'Bundle',id:'Bundle',menu:this.bundles});
      if (this.items.length) {
        if (this.itemCategories.length) {
          const categories = this.createHierarchyList(this.itemCategories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[types.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      if (this.spirits.length)
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits});
      if (this.wines.length)
        types.push({name:'Wine',id:'Wine',menu:this.wines});
      if (this.nonExcises.length)
        types.push({name:'Non-Excise',id:'NonExcise',menu:this.nonExcises});
      return types;
    },
    addOkay() {
      return this.types.length;
    },
    lineHeaders() {
      const headers =  [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                        { text: 'Item / Description', value: 'item', width:'30%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                        { text: 'SKU', value: 'sku' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                        { text: 'Quantity', value: 'quantity' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                        { text: 'Unit price', value: 'price' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                        { text: 'Amount', value: 'amount' ,width:'12%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                        { text: 'Tax', value: 'taxes' ,width:'10.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true}];
      return headers;
    },
    actualSubtotal() {
      return this.estimate.taxInclusive
              ? this.$roundNumber(this.estimate.lines.reduce((total,line) =>
                    total + line.amount/ (1+(this.taxes.find(tax => tax.id === line.taxCodeId)?.salesRate??0)/100), 0))
              : this.subTotal;
    },
    discountValue() {
      if (this.estimate.discount.percent)
        return this.$roundNumber(this.actualSubtotal * this.estimate.discount.amount/100);
      return this.estimate.discount.amount;
    },
    total() {
      return this.actualSubtotal - this.discountValue + this.estimate.taxLines.reduce((total,tax) => tax.amount + total, 0);
    }
  },
  watch: {
    estimate: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    },
    'preferences.useNumber': function() {
      if (!this.preferences.useNumber)
        delete this.estimate.number;
    },
    'estimate.salesRepId'() {
      const salesRep = this.salesReps.find(rep => rep.id === this.estimate.salesRepId) || '';
      if (this.preferences.defaultCcSalesRep)
        this.estimate.cc = salesRep.email;
      if (this.preferences.defaultBccSalesRep)
        this.estimate.bcc = salesRep.email;
    }
  },
  async created() {
    this.loading = true;
    if (this.estimateId || this.copyEstimateId)
      this.itemProgress = 0;
    try {
      this.province = await import(`@/views/provinces/${this.company.province}/sales/shipments.js`);
    // eslint-disable-next-line no-empty
    } catch (error) {}

    const data = await Promise.all([
      this.$store.dispatch('customers', !this.estimateId),
      this.$store.dispatch('products'),
      this.$store.dispatch('items'),
      this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
//      this.$lazyListLoad(this.products, 'products2', 'id,name,category{id}', 100, !this.estimateId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.bundles, 'bundles2', 'id,name,price,inBondPrice,tax{id},sellingDescription,sku,displayComponents', 100, !this.estimateId ? [{field:'active', comparator:'eq', value:'true'}] : []),
//      this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,precision,category{id}', 100, [{field:'sold', comparator:'eq', value:'true'}, ...(!this.estimateId ? [{field:'active', comparator:'eq', value:'true'}] : [])]),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.estimateId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ?  this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.estimateId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.estimateId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name,email,restricted,userId', 100, this.estimateId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,groupTaxes', 100, this.estimateId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'salesRate', comparator:'ne', value:''}])
    ]);
    this.customers = data[0];
    this.products = this.estimateId ? data[1] : data[1].filter(product => product.active);
    this.items = this.estimateId ? data[2].filter(item => item.sold) : data[2].filter(item => item.sold && item.active);

    const results = await this.$apiCall(`query SaleEstimatesEdit($edit:Boolean!, $id:ID!) {
                                              estimate(id:$id) @include(if:$edit) {
                                                id,customer{id},shipToAddress,billToAddress,email,cc,bcc,date,expires,status,memo,privateNote,
                                                taxInclusive,number,salesRep{id},qbo,exchangeRate
                                                lines {
                                                  type,id,inBondProduct,cases,description,sku,quantity,unitPrice,amount,taxCodeId
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                discount {percent, amount}
                                              }
                                              salesPreferences {
                                                useNumber,defaultMessage,allowDiscounts
                                                alwaysInBond,defaultQBO,defaultTaxInclusive
                                                defaultCc,defaultBcc,defaultCcSalesRep,defaultBccSalesRep
                                              }
                                              estimateNextNumber
                                          }`,
                                        {edit:!!this.estimateId || !!this.copyEstimateId,
                                         id:this.estimateId ?? this.copyEstimateId ?? ''}
                                         );
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.restrictedToSalesRep = this.salesReps.find(rep => rep.userId === this.$store.state.user.sub && rep.restricted)?.id || '';

    if (this.estimateId || this.copyEstimateId) {
      let key = Date.now();
      results.data.estimate.lines.forEach(item => item.key = key++);
      results.data.estimate.customerId = results.data.estimate.customer.id;
      delete results.data.estimate.customer;
      results.data.estimate.salesRepId = results.data.estimate.salesRep?.id ?? '';
      delete results.data.estimate.salesRep;
      this.estimate = this.estimateId
                      ? results.data.estimate
                      : {...results.data.estimate, id:null, date:this.estimate.date, expires:this.estimate.expires, status:this.estimate.status, number:results.data.estimateNextNumber};
      let n = 0;
      for (const line of this.estimate.lines) {
        if (line.type === 'BulkContainer' || line.type === 'Tank')
          line.type = 'alcohol';
        await this.setLineDetails(line, true);

        if (this.estimate.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.estimate.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
        this.itemProgress = (n++ / this.estimate.lines.length) * 100;
      }
      this.calculateTotals(!this.estimate.taxLines);
      this.estimateNumber = this.estimate.number;
      this.edit = !!this.copyEstimateId
    }
    this.preferences = results.data.salesPreferences;
    if (!this.estimateId && !this.copyEstimateId) {
      this.edit = true;
      this.estimate.number = results.data.estimateNextNumber;
      this.estimate.salesRepId = this.restrictedToSalesRep;
      this.estimate.taxInclusive = this.preferences.defaultTaxInclusive;
      this.estimate.qbo = this.preferences.defaultQBO;
      this.estimate.customerId = this.customerId;
      this.estimate.memo = this.preferences.defaultMessage;
      this.estimate.cc = this.preferences.defaultCc;
      this.estimate.bcc = this.preferences.defaultBcc;
      this.changeCustomer();
    }
    this.customerTypes = await this.buildCustomerTypes();
    this.$nextTick(() => {this.dirty = false});
    this.itemProgress = 100;
    this.loading = false;
  },

  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        oldIndex -= _self.moveOffset;
        const lineSelected = _self.estimate.lines.splice(oldIndex, 1)[0];
        _self.estimate.lines.splice(newIndex, 0, lineSelected);
      },
      onStart({oldIndex}) {
        let offset = 0;
        for (let index = 0; index < oldIndex-offset; index++) {
          const line = _self.estimate.lines[index];
          if (_self.expanded.some(e => e.key === line.key))
            offset += 1;
        }
        _self.moveOffset = offset;
        _self.expanded.splice(0, _self.expanded.length);
      }
    })

    if (!this.estimateId)
      this.$refs.form.validate();
    setTimeout(() => this.$refs.customer.focus(), 1000);
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    async getExchangeRate() {
      if (this.customer && this.customer.currency !== this.company.settings.homeCurrency) {
        const results = await this.$apiCall(`query SaleEstimatesEdit($code:String!,$date:Date!) {
                                                currencyRate(code:$code,date:$date)
                                              }`,
                                              {code:this.customer.currency,date:this.estimate.date});
        if (!results.error)
          this.estimate.exchangeRate = results.data.currencyRate;
      }
    },
    itemDescription(item) {
      const type = this.types.find(type => type.id === item.type);
      if (!type)
        return 'MISSING';

      const lists = {'Product':[this.products], 'Item': [this.items], 'Bundle': [this.bundles], 'Spirit':[this.spirits], 'Wine':[this.wines], 'NonExcise':[this.nonExcises]};
      let name;
      for (const list of lists[type.id]) {
        name = list.find(i => i.id === item.id)
        if (name)
          break;
      }

      return `${type.name}: ${name?.name}`;
    },
    itemTax(item) {
      if (this.connectedToAccounting && !item.taxCodeId)
        return 'Required';
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    isBulkContainer(line) {
      return line.type === 'alcohol' && this.bulkContainers.some(b => b.id === line.id);
    },
    menuForProducts(products) {
      if (this.productCategories.length) {
        const categories = this.createHierarchyList([...this.productCategories]);
        for (const category of categories) {
          const sub = products.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
        const p = categories;
        const leftOver = products.filter(product => !product.category?.id);
        if (leftOver.length)
          p.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
        return p;
      }
      else
        return products;
    },
    vesselsMenu(vessels) {
      vessels.forEach(vessel => {
        if (vessel.contents) {
          let amount = '(empty)';
          if (vessel.contents.alcoholType && vessel.contents.volume) {
            const spirits = vessel.contents.alcoholType === 'Spirit';
            amount = `${this.$number(vessel.contents.volume, 3)} ${spirits?'LAA':'litres'} ${vessel.contents.alcoholName}`;
          }
          vessel.subTitle = amount;
        }
      });
      return vessels;
    },
    calculateTotals(calculateTaxes) {
      this.subTotal = this.estimate.lines.reduce((total,line) =>
        total + (line.items ? line.items.reduce((total,line) => total+line.amount, 0) : line.amount)
      , 0);

      if (calculateTaxes) {
        this.estimate.taxLines = [];
        this.estimate.lines.forEach(line => {
          if (line.items)    // Process bundle items
            line.items.forEach(item => this.calculateTaxesOnLine(item));
          else
            this.calculateTaxesOnLine(line);
        });
      }
    },
    calculateTaxesOnLine(line) {
      const discount = 1 - (this.subTotal ? (this.estimate.discount.percent ? this.estimate.discount.amount/100 : this.estimate.discount.amount / this.subTotal) : 0);
      if (line.taxCodeId) {
        const taxDefinition = this.taxes.find(t => t.id === line.taxCodeId);
        if (!taxDefinition)
            return;
        for (const taxCodeId of taxDefinition.groupTaxes ?? [line.taxCodeId]) {
          let tax = this.estimate.taxLines.find(tax => tax.id === taxCodeId);
          if (!tax) {
            const taxDefinitionToUse = this.taxes.find(t => t.id === taxCodeId);
            if (!taxDefinitionToUse)
                return;
            tax = {id:taxCodeId,name:taxDefinitionToUse.name,percent:taxDefinitionToUse.salesRate,amount:0,onAmount:0};
            this.estimate.taxLines.push(tax);
          }
          tax.onAmount += discount * (this.estimate.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount);
          tax.amount += this.$roundNumber(discount * (this.estimate.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100));
        }
      }
    },
    suffix(line) {
      if (line.type === 'Product') {
        if (line.cases)
          return 'case(s)';
        const product = this.products.find(p => p.id === line.id);
        if (product?.bottlesPerUnit > 1)
          return 'unit(s)';
        if (product?.mixedPack)
          return 'pack(s)';
        if (product?.containerType === 'Can')
          return 'can(s)';
        return 'bottle(s)';
      }
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.trackUnits;
      if (line.type === 'Bundle')
        return '';
      return null;
    },
    precision(line) {
      if (line.type === 'Product')
        return 0;
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.precision;
      if (line.type === 'Bundle')
        return 0;
      return 3;
    },
    customerHint(customer) {
      const noType = customer?.type === 'None';
      return `${noType ? '' : `Type: ${this.customerTypes.find(type => type.value === customer?.type)?.text}`}${customer?.category?.id ? `${noType?'': ', '}Category: ${customer.category.name}` : ''}`;
    },
    async addLine() {
      const line = {key:Date.now(),type:'',id:'',inBondProduct:this.preferences.alwaysInBond,cases:false,description:'',errorMessage:null,amount:0,unitPrice:0,quantity:1,taxCodeId:null};
      this.estimate.lines.push(line);
      this.setLineDetails(line, false);
      this.selectedItem = line.key;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteLine(index) {
      this.estimate.lines.splice(index, 1);
      this.calculateTotals(true);
    },

    async loadProduct(line) {
      const product = this.products.find(product => product.id === line.id);
      if (!product) {
        line.type = '';
        line.id = '';
        return;
      }
      if (product.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesEstimatesEdit($id:ID!) {
                                            product(id:$id) {
                                              sku,bottleSize,caseSize,bottlesPerUnit
                                              mixedPack,containerType
                                              defaultBondedPrice,defaultBondedTax{id},sellingBondedDescription,
                                              defaultPrice,defaultTax{id},sellingDescription,
                                              bondedCustomers{customerId,tax{id},price,sku}
                                              customers{customerId,tax{id},price,sku}
                                            }
                                          }`,
                                          {id:product.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(product, results.data.product);
        product.loaded = true;
      }
    },

    async loadItem(line) {
      const item = this.items.find(item => item.id === line.id);
      if (!item) {
        line.type = '';
        line.id = '';
        return;
      }
      if (item.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesEstimatesEdit($id:ID!) {
                                            item(id:$id) {
                                              sku,defaultPrice,defaultTax{id},sellingDescription,
                                              customers{customerId,tax{id},price,sku}
                                            }
                                          }`,
                                          {id:item.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data.item);
        item.loaded = true;
      }
    },

    async loadAlcoholItem(line) {
      const item = {'Spirit':this.spirits,'Wine':this.wines,'NonExcise':this.nonExcises}[line.type].find(item => item.id === line.id);
      if (!item) {
        line.type = '';
        line.id = '';
        return;
      }
      if (item.loaded)
        return;

      if (line.type === null)
        return;
      let type = line.type.toLowerCase();
      type  = type === 'nonexcise' ? 'nonExcise' : type;

      this.loading = true;
      const results = await this.$apiCall(`query SalesEstimatesEdit($id:ID!) {
                                             ${type}(id:$id) {
                                               sku,defaultPrice,defaultTax{id},sellingDescription,customers{customerId,tax{id},price,sku}
                                              }
                                            }`,
                                          {id:item.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data[type]);
        item.loaded = true;
      }
    },

    async loadBundle(line) {
      const bundle = this.bundles.find(bundle => bundle.id === line.id);
      if (!bundle) {
        line.type = '';
        line.id = '';
        return;
      }
      if (bundle.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesEstimatesEdit($id:ID!) {
                                            bundle(id:$id) {
                                              items {name,type,id,quantity,price}
                                            }
                                          }`,
                                          {id:bundle.id});
      this.loading = false;
      if (!results.error) {
        Object.assign(bundle, results.data.bundle);
        bundle.loaded = true;
      }
    },

    changeLineType(line) {
      line.unitPrice = 0;
      line.taxCodeId = null;
      line.description = '';
      line.sku = '';
      this.setLineDetails(line, false);
    },

    async setLineDetails(line, init) {
      if (line.type !== 'Bundle')
        delete line.items;

      if (line.id === '') {
        this.$nextTick(() => this.$refs.form?.validate());
        return;
      }

      if (line.type === 'Product')
        await this.loadProduct(line);
      else if (line.type === 'Item')
        await this.loadItem(line);
      else if (line.type === 'Bundle')
        await this.loadBundle(line);
      else if (line.type === 'Spirit' || line.type === 'Wine' || line.type === 'NonExcise')
        await this.loadAlcoholItem(line);


      if (!init) {
        let customerPricing;
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          line.unitPrice = !this.company.settings.lotTrackingForProducts && this.customerTypes.find(type => type.value === this.customer?.type)?.prefInBond ? bundle.inBondPrice : bundle.price;
          line.taxCodeId = bundle.tax?.id;
          this.calculateUnitPrice(line);
          line.sku = bundle.sku;
          this.changeQuantityPrice(line);
        }
        else if (line.type === 'Product') {
          line.inBondProduct = !!this.customer && this.customerTypes.find(type => type.value === this.customer.type).prefInBond || this.preferences.alwaysInBond;
          line.cases = this.customerTypes.find(type => type.value === this.customer?.type)?.prefCases ?? false;
          this.changeProductBonded(line);
        }

        else if (line.type === 'Item') {
          const item = this.items.find(item => item.id === line.id);
          customerPricing = item.customers.find(customer => customer.customerId === this.estimate.customerId) ??
                            item.customers.find(customer => customer.customerId === this.customer?.category?.id);
          line.unitPrice = customerPricing?.price || item.defaultPrice;
          line.taxCodeId = customerPricing?.tax.id || item.defaultTax?.id;
          this.calculateUnitPrice(line);
          line.description = line.description || item.sellingDescription || item.name;
          line.sku = customerPricing?.sku || item?.sku;
          this.changeQuantityPrice(line);
        }

        else {
          const item = {'Spirit':this.spirits,'Wine':this.wines,'NonExcise':this.nonExcises}[line.type].find(item => item.id === line.id);
          customerPricing = item.customers.find(customer => customer.customerId === this.estimate.customerId) ??
                            item.customers.find(customer => customer.customerId === this.customer?.category?.id);
          line.unitPrice = customerPricing?.price || item.defaultPrice;
          line.taxCodeId = customerPricing?.tax.id || item.defaultTax?.id;
          this.calculateUnitPrice(line);
          line.description = line.description || item.sellingDescription || item.name;
          line.sku = customerPricing?.sku || item?.sku;
          this.changeQuantityPrice(line);
        }

      }
      this.$nextTick(() => this.$refs.form?.validate());
    },
    isProductLoaded(line) {
      return this.products.find(product => product.id === line.id)?.loaded;
    },
    changeProductBonded(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = line.inBondProduct && !!this.customer && this.customerTypes.find(type => type.value === this.customer.type).prefInBond;
      line.description = product[inBondPricing?'sellingBondedDescription':'sellingDescription'] || product.name;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      const customerPricing = productCustomers.find(customer => customer.customerId === this.estimate.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.sku = customerPricing?.sku || product?.sku;
      this.setProductPrice(line);
      this.$nextTick(() => this.$refs.form.validate());
    },
    async setProductPrice(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = line.inBondProduct && !!this.customer && this.customerTypes.find(type => type.value === this.customer.type).prefInBond;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      const customerPricing = productCustomers.find(customer => customer.customerId === this.estimate.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.unitPrice = customerPricing?.price || product[inBondPricing?'defaultBondedPrice':'defaultPrice'];
      line.taxCodeId = customerPricing?.tax.id || product[inBondPricing?'defaultBondedTax':'defaultTax']?.id;
      if (!customerPricing && this.province && !inBondPricing)
        await this.province.pricing(line, product, this.customer, this.taxes);
      if (inBondPricing) {
        if (!line.cases)
          line.unitPrice = line.unitPrice / product.caseSize;
      }
      else if (line.cases)
        line.unitPrice = line.unitPrice / product.bottlesPerUnit * product.caseSize;
      this.calculateUnitPrice(line);
      this.changeQuantityPrice(line);
    },
    calculateUnitPrice(line) {
      if (this.estimate.taxInclusive && line.taxCodeId) {
        const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
        line.unitPrice += line.unitPrice * tax.salesRate/100;
        line.amount = this.$roundNumber(line.unitPrice * line.quantity);
      }
    },
    toggleTaxInclusive() {
      for (const line of this.estimate.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.estimate.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.salesRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTotals(true);
    },
    async newCustomer(customer) {
      this.customers = await this.$store.dispatch('customers', !this.estimateId);
      this.estimate.customerId = customer.id;
      this.getExchangeRate();
      this.estimate.salesRepId = this.customer.salesRep?.id;
    },
    async changeCustomer() {
      if (!this.customer) {
        this.estimate.email = '';
        this.estimate.billToAddress = '';
        this.estimate.shipToAddress = '';
        this.estimate.salesRepId = '';
        return;
      }

      if (!this.customer.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesEstimatesEdit($id:ID!) {
                                                customer(id:$id) {
                                                  id,name,email,currency,term{id},salesRep{id}
                                                  billingAddress{line1,line2,city,province,postalCode}
                                                  shippingAddress{line1,line2,city,province,postalCode}
                                                }
                                              }`,
                                              {id:this.customer.id});
        this.loading = false;
        if (!results.error) {
          this.customer.loaded = true;
          Object.assign(this.customer, results.data.customer);
        }
      }
      this.estimate.email = this.customer.email;
      const billToAddress = this.customer.billingAddress;
      this.estimate.billToAddress = this.customer.name + '\n' + billToAddress.line1 + '\n' + (billToAddress.line2 ? billToAddress.line2 + '\n' : '') +
                              billToAddress.city + ' ' + billToAddress.province + '  ' + billToAddress.postalCode;
      const shipToAddress = this.customer.shippingAddress;
      this.estimate.shipToAddress = this.customer.name + '\n' + shipToAddress.line1 + '\n' + (shipToAddress.line2 ? shipToAddress.line2 + '\n' : '') +
                              shipToAddress.city + ' ' + shipToAddress.province + '  ' + shipToAddress.postalCode;
      for (const line of this.estimate.lines)
        this.setLineDetails(line, false);
      this.estimate.salesRepId = this.customer.salesRep?.id;
      this.getExchangeRate();
      this.calculateTotals(true);
    },
    changeQuantityPrice(line, parentLine) {
      if (line.items)
        line.items.forEach(l => this.changeQuantityPrice(l, line));
      else
        line.amount = this.$roundNumber(line.quantity * line.unitPrice * (parentLine?.quantity || 1));
      this.calculateTotals(true);
    },
    changeAmount(line) {
      line.unitPrice = line.quantity ? this.$roundNumber(line.amount / line.quantity) : 0;
      this.calculateTotals(true);
    },
    cancel() {
      this.$router.back();
    },
    isLineIncomplete(line) {
      return !line.type || !line.id || !line.description ||
              line.quantity < 0 && line.type !== 'Item' ||
              line.unitPrice < 0 ||
              line.amount < 0 && line.type !== 'Item' ||
              (this.connectedToAccounting && !line.taxCodeId && !line.items?.length) ||
              line.items?.length && line.items.some(subLine => this.isLineIncomplete(subLine));
    },
    async save() {
      const incomplete = this.estimate.lines.some(line => this.isLineIncomplete(line));
      if (!this.$refs.form.validate() || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;

      // Build request
      // eslint-disable-next-line no-unused-vars
      const {lines, ...input} = this.estimate;
      input.lines = this.estimate.lines.map(line => {
        let amount = line.amount;
        if (this.estimate.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          amount = this.$roundNumber(line.amount / (1 + tax.salesRate/100));
        }
        return {...line, amount, key:undefined, errorMessage:undefined, unitPrice:undefined};
      });
      const results = await this.$apiCall(`mutation SalesEstimatesEdit($input: EstimateInput!) {
                                             estimateCrupdate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.estimate.id?'Updated':'Created'} estimate to ${this.customer.name}`});
      }
    }
  }
}
</script>